<!-- Scrollable section -->
<div class="entity-edit-wrapper">
  <app-base-modal dialogTitle="Change Affected Entity" (closeEvent)="cancelEntityEdit()">
    <!-- Input fields section  -->
    <div class="form-input-fields">
      <h3 class="input-fields-title">Select Search Criteria</h3>

      <mat-radio-group [(ngModel)]="searchMode" class="radio-group" (click)="searchModeSelected()">
        <mat-radio-button value="companyName">Search by Company Name</mat-radio-button>
        <mat-radio-button value="id">Search by Company ID</mat-radio-button>
      </mat-radio-group>

      <!-- Form used when we are going to search by dnb id only -->
      @if (searchMode === 'id') {
        <form #editForm="ngForm" [formGroup]="searchByDnBIdFormData" class="form-search-by-dnb-id">
          <mat-form-field class="dnb-id-input-field" appearance="outline" floatLabel="always">
            <mat-label>DnB Id</mat-label>
            <input type="text" matInput formControlName="dnbId" />
            @if (searchByDnbIdInvalid) {
              <mat-error> DnB ID is required. </mat-error>
            }
          </mat-form-field>

          <button
            mat-flat-button
            class="button-primary flex-keep-size"
            type="submit"
            (click)="search()"
            [disabled]="searchByDnbIdInvalid"
          >
            Search
          </button>
        </form>
      }

      <!-- Form used when we are going to search by company name, country and domain -->
      @if (searchMode === 'companyName') {
        <form #editForm="ngForm" [formGroup]="searchByCompanyNameFormData" class="form-search-by-company-name">
          <mat-form-field class="flex-take-rest-of-space" appearance="outline" floatLabel="always">
            <mat-label>Company name:</mat-label>
            <input type="text" matInput formControlName="companyName" />
            @if (searchByCompanyNameInvalid) {
              <mat-error> Company name is required. </mat-error>
            }
          </mat-form-field>

          <mat-form-field class="flex-take-rest-of-space" appearance="outline" floatLabel="always">
            <mat-label>Country</mat-label>

            <input type="text" matInput formControlName="country" [matAutocomplete]="auto" />

            <mat-autocomplete #auto="matAutocomplete" [displayWith]="displayCountryName.bind(this)">
              @for (country of filteredCountries$ | async; track country.value) {
                <mat-option [value]="country.value">{{ country.label }}</mat-option>
              }
            </mat-autocomplete>
          </mat-form-field>

          <mat-form-field class="flex-take-rest-of-space" appearance="outline" floatLabel="always">
            <mat-label>Domain:</mat-label>
            <input type="text" matInput formControlName="domain" />
          </mat-form-field>

          <button
            mat-flat-button
            class="button-primary flex-keep-size"
            type="submit"
            (click)="search()"
            [disabled]="searchByCompanyNameInvalid"
          >
            Search
          </button>
        </form>
      }
    </div>

    <!-- Search results table -->
    <table mat-table [dataSource]="companyMatches">
      <!-- Radio Button -->
      <ng-container matColumnDef="selectRadio">
        <th mat-header-cell *matHeaderCellDef>Select</th>
        <td mat-cell *matCellDef="let row">
          <mat-radio-group [(ngModel)]="activeEntity">
            <mat-radio-button [value]="row"></mat-radio-button>
          </mat-radio-group>
        </td>
      </ng-container>

      <!-- Company Name -->
      <ng-container matColumnDef="name">
        <th mat-header-cell *matHeaderCellDef>Company Name</th>
        <td mat-cell *matCellDef="let element">{{ element.name }}</td>
      </ng-container>

      <!-- Country Code -->
      <ng-container matColumnDef="countryCode">
        <th mat-header-cell *matHeaderCellDef>Country ISO</th>
        <td mat-cell *matCellDef="let element">{{ element.countryCode }}</td>
      </ng-container>

      <!-- Status -->
      <ng-container matColumnDef="status">
        <th mat-header-cell *matHeaderCellDef>Status</th>
        <td mat-cell *matCellDef="let element">
          <div
            [ngClass]="{
              'company-status-green': element?.status?.toLowerCase() === 'active',
              'company-status-red': element?.status && element.status.toLowerCase() !== 'active',
            }"
          >
            {{ element.status }}
          </div>
        </td>
      </ng-container>

      <!-- Company ID -->
      <ng-container matColumnDef="dnbId">
        <th mat-header-cell *matHeaderCellDef>Company ID</th>
        <td mat-cell *matCellDef="let element">{{ element.dnbId }}</td>
      </ng-container>

      <!-- Revenue -->
      <ng-container matColumnDef="revenue">
        <th mat-header-cell *matHeaderCellDef>Revenue (USD)</th>
        <td mat-cell *matCellDef="let element">{{ element.revenue }}</td>
      </ng-container>

      <!-- City -->
      <ng-container matColumnDef="city">
        <th mat-header-cell *matHeaderCellDef>City</th>
        <td mat-cell *matCellDef="let element">{{ element.city }}</td>
      </ng-container>

      <!-- ZIP -->
      <ng-container matColumnDef="zipCode">
        <th mat-header-cell *matHeaderCellDef>ZIP</th>
        <td mat-cell *matCellDef="let element">{{ element.zipCode }}</td>
      </ng-container>

      <!-- Address -->
      <ng-container matColumnDef="headquarterAddress">
        <th mat-header-cell *matHeaderCellDef>Address</th>
        <td mat-cell *matCellDef="let element">{{ element.headquarterAddress }}</td>
      </ng-container>

      <!-- Website -->
      <ng-container matColumnDef="domain">
        <th mat-header-cell *matHeaderCellDef>Website</th>
        <td mat-cell *matCellDef="let element">{{ element.domain }}</td>
      </ng-container>

      <!-- Ultimate Parent Company Name -->
      <ng-container matColumnDef="ultimateParentCompanyName">
        <th mat-header-cell *matHeaderCellDef>Ultimate Parent Company Name</th>
        <td mat-cell *matCellDef="let element">{{ element.ultimateParentCompanyName }}</td>
      </ng-container>

      <tr mat-header-row *matHeaderRowDef="companyMatchColumns"></tr>
      <tr
        [ngClass]="{
          'table-row': true,
          'selected-row': activeEntity === row,
        }"
        mat-row
        *matRowDef="let row; columns: companyMatchColumns"
        (click)="onRowSelected(row)"
      ></tr>
    </table>

    <!-- Additional style showing that there are no records to display in the results table -->
    @if (!companyMatches || companyMatches.data.length === 0) {
      <div class="no-data-yet">No data yet</div>
    }

    @if (isLoading) {
      <div class="spinner-wrapper">
        <mat-spinner class="spinner" [diameter]="32"></mat-spinner>
      </div>
    }

    <!-- Save and Cancel buttons -->
    <!-- This is projected by the ng-content of the app-base-modal -->
    <div panel-footer>
      <button mat-flat-button class="button-link" type="button" (click)="cancelEntityEdit()">Cancel</button>
      <button mat-flat-button class="button-primary" type="button" (click)="saveEntity()" [disabled]="!activeEntity">
        Save
      </button>
    </div>
  </app-base-modal>
</div>
